// @ts-ignore
import { useMelwaysInfo } from '@seek/melways-react';
import { adapter, getScriptTag } from '@seek/tealium-adapter';
import { useTypedSelector } from 'src/reducers';

const jobsrecsMapping = ['wru', 'newjob'];

const events: Record<string, string> = {
  // views
  UNSUBSCRIBED_SUCCESS_PAGE_VIEWED:
    'notification_unsubscribe_success_displayed',
  UNSUBSCRIBED_FAILED_PAGE_VIEWED: 'notification_unsubscribe_failed_displayed',
  SUBSCRIBED_FAILED_PAGE_VIEWED: 'notification_subscribe_failed_displayed',
  SUBSCRIBED_SUCCESS_PAGE_VIEWED: 'notification_subscribe_success_displayed',

  // links
  NOTI_PREF_TOGGLED: 'toggle_pressed',
  NOTI_PREF_OPTION_SELECTED: 'option_selection_pressed',
};

const PREF_PAGE_TITLE = 'notification settings';

const tealiumAdapter = adapter();

const convertToApacProductName = (product: string | undefined) => {
  if (product === undefined) {
    return undefined;
  }

  let apacProductName = product;

  if (jobsrecsMapping.includes(product.toLowerCase())) {
    apacProductName = 'jobrecs';
  }

  return apacProductName;
};

export const getTealiumScriptTag: (env: string | null) => string = (env) =>
  env
    ? getScriptTag({
        account: 'seek',
        profile: 'houston',
        environment: env,
      })
    : '';

let candidateId: number;
let profileGuid: string;

let trackingContext: TealiumTrackingContext;

interface TealiumTrackingContext {
  brand: string;
  siteCountry: string;
  zone: string;
  siteLanguage: string;
}

const commonPageFields = () => ({
  siteSection: 'myaccount',
  loginId: candidateId,
  profileGuid,
  isLoggedIn: Boolean(candidateId),
  userAgent: global?.navigator?.userAgent,
  ...trackingContext,
});

export const setCandidateId = (
  id: number,
  emailCorrelationId: string,
): void => {
  candidateId = id;
  profileGuid = emailCorrelationId;
};

export const setTrackingContext = (
  brand: string,
  country: string,
  zone: string,
  language: string,
): void => {
  trackingContext = {
    brand,
    siteCountry: country,
    zone,
    siteLanguage: language,
  };
};

export const useSetTrackingContext = () => {
  const brand = useTypedSelector((state) => state.page.pageAnalytics.brand);
  const country = useTypedSelector(
    (state) => state.page.pageAnalytics.siteCountry,
  );
  const zone = useTypedSelector((state) => state.page.pageAnalytics.zone);
  const { language } = useMelwaysInfo();

  setTrackingContext(brand, country, zone, language);
};

export const trackToggle = (
  product: string,
  channel: string,
  subscribed: boolean,
  frequency?: string,
): void => {
  const trackingProduct = convertToApacProductName(product);

  tealiumAdapter.link(events.NOTI_PREF_TOGGLED, {
    ...commonPageFields(),
    currentPage: PREF_PAGE_TITLE,
    eventName: events.NOTI_PREF_TOGGLED,
    toggleType: `${trackingProduct} ${channel}`,
    toggleState: subscribed ? 'on' : 'off',
    ...(frequency ? { notificationFrequency: frequency } : {}),
    isExitLink: false,
  });
};

export const trackOptionSelectionPressed = ({
  product,
  channel,
  optionSelectionType,
  optionSelected,
}: {
  product: string;
  channel: string;
  optionSelectionType: string;
  optionSelected: string;
}): void => {
  const trackingProduct = convertToApacProductName(product);

  tealiumAdapter.link(events.NOTI_PREF_OPTION_SELECTED, {
    ...commonPageFields(),
    currentPage: PREF_PAGE_TITLE,
    eventName: events.NOTI_PREF_OPTION_SELECTED,
    optionSelectionType,
    optionSelected,
    optionSelectionName: `${trackingProduct} ${channel} frequency`,
    currentPageSection: `${trackingProduct} ${channel}`,
    isExitLink: false,
  });
};

export const trackUnsubscribeSuccessPageLoad = (
  product: string | undefined,
  channel: string | undefined,
) => {
  const trackingProduct = convertToApacProductName(product);

  tealiumAdapter.view(events.UNSUBSCRIBED_SUCCESS_PAGE_VIEWED, {
    ...commonPageFields(),
    currentPage: 'notification unsubscribe success',
    notificationType: trackingProduct,
    notificationChannel: channel,
  });
};

export const trackUnsubscribeFailedPageLoad = () => {
  tealiumAdapter.view(events.UNSUBSCRIBED_FAILED_PAGE_VIEWED, {
    ...commonPageFields(),
    currentPage: 'notification unsubscribe failed',
  });
};

export const trackSubscribeSuccessPageLoad = (
  product: string | undefined,
  channel: string | undefined,
) => {
  const trackingProduct = convertToApacProductName(product);

  tealiumAdapter.view(events.SUBSCRIBED_SUCCESS_PAGE_VIEWED, {
    ...commonPageFields(),
    currentPage: 'notification subscribe success',
    notificationType: trackingProduct,
    notificationChannel: channel,
  });
};

export const trackSubscribeFailedPageLoad = () => {
  tealiumAdapter.view(events.SUBSCRIBED_FAILED_PAGE_VIEWED, {
    ...commonPageFields(),
    currentPage: 'notification subscribe failed',
  });
};
